import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, FileText, Search, X } from 'lucide-react';
import { useJobsStore } from '../../store/jobsStore';
import { ResearchJob } from '../../types';
import { Card } from '../ui';

interface MemoPickerProps {
  open: boolean;
  onClose: () => void;
  /** Studio tab to land on: Memo Studio → 'chat', draft-from-memo → 'drafts'.
      Ignored when `onSelect` is provided. */
  tab?: 'chat' | 'drafts';
  title: string;
  /** Selection mode (spec §4.4): instead of navigating, hand the picked memo
      back to the caller — used by the unified drafting intake's
      "Base it on a research memo" attachment. */
  onSelect?: (job: ResearchJob) => void;
}

/**
 * Modal chooser for completed memos — Memo Studio and Draft-from-Research
 * open from a memo the USER picks, not silently from the latest one.
 */
export function MemoPicker({ open, onClose, tab, title, onSelect }: MemoPickerProps) {
  const navigate = useNavigate();
  const { jobs, fetchJobs } = useJobsStore();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    setQuery('');
    // The dashboard cache only holds the 10 most recent jobs — widen it so
    // older completed memos are pickable too.
    fetchJobs(100);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, fetchJobs, onClose]);

  const completed = useMemo(() => jobs.filter((j) => j.status === 'completed'), [jobs]);
  const shown = query.trim()
    ? completed.filter((j) => j.title.toLowerCase().includes(query.trim().toLowerCase()))
    : completed;

  if (!open) return null;

  // Portal to <body>: the dashboard root is animated, which would otherwise
  // become the containing block for this fixed overlay and leave the sidebar
  // uncovered (backdrop clicks would fall through to nav links).
  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-legal-900/50 flex items-end sm:items-center justify-center p-0 sm:p-6 animate-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <Card
        padded={false}
        className="w-full max-w-md max-h-[80vh] sm:max-h-[70vh] flex flex-col rounded-b-none sm:rounded-card"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-legal-100">
          <h3 className="font-semibold text-legal-900">{title}</h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded-md text-legal-400 hover:text-legal-700 hover:bg-legal-50 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {completed.length > 7 && (
          <div className="px-5 py-2.5 border-b border-legal-100">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-legal-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filter memos…"
                autoFocus
                className="w-full pl-8 pr-3 py-1.5 text-sm border border-legal-200 rounded-md text-legal-800 placeholder-legal-400 focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-5 py-1">
          {shown.length === 0 ? (
            <p className="text-sm text-legal-400 py-4">
              {query ? 'No memos match that.' : 'No completed memos yet.'}
            </p>
          ) : (
            shown.map((job) => (
              <button
                key={job.id}
                type="button"
                data-track={`memo-picker-${onSelect ? 'select' : tab}`}
                onClick={() => {
                  onClose();
                  if (onSelect) {
                    onSelect(job);
                  } else {
                    navigate(`/app/research/${job.id}/${tab === 'chat' ? 'discuss' : 'drafts'}`);
                  }
                }}
                className="group w-full flex items-center gap-3 text-left px-1 py-2.5 border-b border-legal-100 last:border-b-0"
              >
                <FileText className="h-3.5 w-3.5 text-legal-400 shrink-0" />
                <span className="min-w-0 flex-1 text-sm text-legal-800 truncate group-hover:text-accent-700 transition-colors">
                  {job.title}
                </span>
                <span className="text-xs text-legal-400 shrink-0">
                  {new Date(job.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                </span>
              </button>
            ))
          )}
        </div>

        <div className="px-5 py-3 border-t border-legal-100">
          <Link
            to="/app/research/new"
            className="inline-flex items-center gap-1 text-xs text-legal-500 hover:text-accent-700 transition-colors"
          >
            Run a new research <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </Card>
    </div>,
    document.body
  );
}
